<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { sendCodeApi, sendEmailCodeApi } from '@Api/otp.api';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { MfaStatus } from '@Enums/mfaStatus.enum';
import { OTP_COOLDOWN_MESSAGE } from '@Use/useOtpCodeVerification';

import { GuidedShowcaseLayout, ActionBarLayout } from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';

const { proxy } = getCurrentInstance();
const router = useRouter();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const deviceAuthStore = useDeviceAuthStore();
const { availableChannels } = storeToRefs(deviceAuthStore);

const selectedDeliveryMethod = ref<'sms' | 'voice' | 'email' | null>(null);
const isSending = ref(false);
const errorMessage = ref<string | null>(null);

// Determine headline and preselection based on available channels
const headline = computed(() => {
    if (!availableChannels.value || availableChannels.value.length === 0) {
        return 'Verify your identity';
    }
    const hasPhone = availableChannels.value.some(c => c.channel === 'phone');
    const hasEmail = availableChannels.value.some(c => c.channel === 'email');

    if (hasPhone && hasEmail) {
        return 'Verify your identity';
    } else if (hasEmail) {
        return 'Verify your email address';
    } else {
        return 'Verify your phone number';
    }
});

// Preselect email if it's the only channel
const shouldPreselectEmail = computed(() => {
    if (!availableChannels.value || availableChannels.value.length !== 1) {
        return false;
    }
    return availableChannels.value[0].channel === 'email';
});

// Preselect the Email card when email is the only channel
const initializeSelections = () => {
    if (shouldPreselectEmail.value) {
        selectedDeliveryMethod.value = 'email';
    }
};

// Validation — a delivery method must be chosen
const isValid = computed(() => {
    return selectedDeliveryMethod.value !== null;
});

const sendCode = async () => {
    if (!isValid.value || isSending.value) return;

    isSending.value = true;
    errorMessage.value = null;

    try {
        if (selectedDeliveryMethod.value === 'email') {
            // Email channel: POST /api/AuthFlow/SendEmailCode
            const response = await sendEmailCodeApi();

            deviceAuthStore.setMfaStatus(response.status);

            if (response.status === MfaStatus.Initialized) {
                // Carry the selected email's redacted identifier to the verify-code screen.
                // The 4006 scalar loginHint can be null/stale for the chosen channel, so set it here.
                const emailChannel = availableChannels.value?.find(c => c.channel === 'email');
                if (emailChannel) {
                    deviceAuthStore.setLoginHint(emailChannel.redactedIdentifier);
                }
                deviceAuthStore.setLastDeliveryType('Email');
                proxy.$apptoast('An access code is being sent to your email.');
                await router.push({ name: 'verify-email-code' });
                return;
            }

            if (response.status === MfaStatus.Failed) {
                await router.push({ name: 'mfa-failed' });
                return;
            }

            // Cooldown: too many code requests for this destination. Show a clear cooldown
            // message and stay on the chooser — the cooldown is per destination, so another
            // channel may still work.
            if (response.status === MfaStatus.Cooldown) {
                errorMessage.value = OTP_COOLDOWN_MESSAGE;
                return;
            }

            errorMessage.value = 'Unable to send code. Please try again.';
        } else if (selectedDeliveryMethod.value === 'sms' || selectedDeliveryMethod.value === 'voice') {
            // Phone channel: POST /api/AuthFlow/SendCode with SMS or Voice
            const apiDeliveryType = selectedDeliveryMethod.value === 'sms' ? 'Sms' : 'PhoneCall';
            const response = await sendCodeApi({
                phoneNumber: undefined,
                deliveryType: apiDeliveryType,
            });

            deviceAuthStore.setMfaStatus(response.status);

            if (response.status === MfaStatus.Initialized) {
                // Carry the selected phone's redacted identifier to the passcode screen so it
                // displays the right number instead of a blank/stale value.
                const phoneChannel = availableChannels.value?.find(c => c.channel === 'phone');
                if (phoneChannel) {
                    deviceAuthStore.setLoginHint(phoneChannel.redactedIdentifier);
                }
                const deliveryType = selectedDeliveryMethod.value === 'sms' ? 'Sms' : 'PhoneCall';
                deviceAuthStore.setLastDeliveryType(deliveryType as 'Sms' | 'PhoneCall');
                proxy.$apptoast('An access code is being sent to your mobile device.');
                await router.push({ name: 'verify-passcode' });
                return;
            }

            if (response.status === MfaStatus.Failed) {
                await router.push({ name: 'mfa-failed' });
                return;
            }

            // Cooldown: too many code requests for this destination. Show a clear cooldown
            // message and stay on the chooser — the cooldown is per destination, so another
            // channel may still work.
            if (response.status === MfaStatus.Cooldown) {
                errorMessage.value = OTP_COOLDOWN_MESSAGE;
                return;
            }

            errorMessage.value = 'Unable to send code. Please try again.';
        }
    } catch (err) {
        console.error('[Auth MFE] Channel selection code send failed:', err);
        errorMessage.value = 'Unable to send code. Please try again.';
    } finally {
        isSending.value = false;
    }
};

// Initialize on mount
initializeSelections();
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        :title="headline"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :show-back-button="false"
        :steps="['verify-channel', 'verify-code']"
        current-step="0">

        <app-form-content-layout>
            <p class="channel-selection-view__intro">
                Choose how you'd like to receive your code.
            </p>

            <div class="channel-selection-view__cards">
                <template v-for="channel in availableChannels" :key="channel.channel">
                    <!-- Phone channel: one outlined radio per delivery method (SMS, Voice) -->
                    <template v-if="channel.channel === 'phone'">
                        <app-radio
                            v-for="method in channel.deliveryMethods"
                            :key="`phone-${method}`"
                            v-model="selectedDeliveryMethod"
                            class="channel-selection-view__card"
                            :class="{ 'channel-selection-view__card--selected': selectedDeliveryMethod === method }"
                            radio-style="outlined"
                            :native-value="method"
                            :data-id="`delivery-${method}`">
                            <span class="channel-selection-view__label">
                                <span class="channel-selection-view__method-name">
                                    {{ method === 'sms' ? 'Text message' : 'Phone call' }}
                                </span>
                                <span class="channel-selection-view__identifier">
                                    {{ channel.redactedIdentifier }}
                                </span>
                            </span>
                        </app-radio>
                    </template>

                    <!-- Email channel -->
                    <app-radio
                        v-else-if="channel.channel === 'email'"
                        v-model="selectedDeliveryMethod"
                        class="channel-selection-view__card"
                        :class="{ 'channel-selection-view__card--selected': selectedDeliveryMethod === 'email' }"
                        radio-style="outlined"
                        native-value="email"
                        data-id="delivery-email">
                        <span class="channel-selection-view__label">
                            <span class="channel-selection-view__method-name">Email</span>
                            <span class="channel-selection-view__identifier">
                                {{ channel.redactedIdentifier }}
                            </span>
                        </span>
                    </app-radio>
                </template>
            </div>

            <p v-if="errorMessage" class="channel-selection-view__error" role="alert" data-testid="error-message">
                {{ errorMessage }}
            </p>
        </app-form-content-layout>

        <action-bar-layout>
            <app-button
                expanded
                size="large"
                :is-loading="isSending"
                :is-disabled="!isValid"
                data-id="send-code"
                @onClick="sendCode">
                Send Code
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>

    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.channel-selection-view {
    &__intro {
        line-height: 1.5;
        margin-top: 0;
        margin-bottom: 0;
    }

    &__cards {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 1.5rem;
    }

    &__card {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        background-color: $white;
        cursor: pointer;
        transition: all 0.2s ease;

        // The uilib radio row top-aligns its control, which leaves the circle
        // hanging by the first line of a two-line label. Centre it against the
        // whole label instead.
        :deep(.app-radio__row) {
            align-items: center;
        }

        :deep(.radio--withlabel) {
            margin-top: 0;
        }
    }

    &__label {
        flex: 1;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    &__method-name {
        font-weight: 400;
        color: $black-high;
    }

    &__identifier {
        font-size: $caption-text;
        line-height: 1.5;
        color: $black-med;
    }

    &__error {
        color: $red;
        margin: 0.5rem 0;
    }
}
</style>
